import { createTranslator } from 'short-uuid';
// Capabilities stay in this tab and never enter analytics, query strings or form state.
export const referralToken = () => {
  try {
    return (
      window.__upestateReferralToken ||
      window.sessionStorage.getItem('upestate-referral') ||
      undefined
    );
  } catch {
    return window.__upestateReferralToken;
  }
};
const capabilityKey = (email) =>
  `upestate-onboarding:${String(email || '')
    .trim()
    .toLowerCase()}`;
const capabilities = new Map();
const versions = new Map();
export const onboardingToken = (email) => {
  try {
    return (
      capabilities.get(capabilityKey(email)) ||
      window.sessionStorage.getItem(capabilityKey(email)) ||
      undefined
    );
  } catch {
    return capabilities.get(capabilityKey(email));
  }
};
export const rememberOnboardingToken = (email, token) => {
  if (!token) return;
  rememberDocumentCapability(token);
  capabilities.set(capabilityKey(email), token);
  versions.set(capabilityKey(email), (versions.get(capabilityKey(email)) || 0) + 1);
  try {
    window.sessionStorage.setItem(capabilityKey(email), token);
  } catch {
    /* memory fallback */
  }
};
export const onboardingPayload = (email, data) => {
  const token = onboardingToken(email);
  return { ...data, ...(token ? { upestate_onboarding_token: token } : {}) };
};

export const onboardingIdentity = (email) =>
  `${String(email || '')
    .trim()
    .toLowerCase()}:${versions.get(capabilityKey(email)) || 0}:${Boolean(onboardingToken(email))}`;
export const onboardingRequestOptions = (email) => {
  const token = onboardingToken(email);
  return token
    ? { headers: { Authorization: `Bearer ${token}` }, upestateOnboarding: true }
    : undefined;
};

// Persist proof before sending stage 1. A retry after a lost response keeps the
// same proof; neither the shared referral URL nor knowledge of an email is proof.
const creationNonces = new Map();
const creationKey = (email) =>
  `upestate-creation:${String(email || '')
    .trim()
    .toLowerCase()}`;
// Read-only: checking whether recovery is possible must never mint replacement proof.
export const retainedOnboardingCreationNonce = (email) => {
  const key = creationKey(email);
  try {
    return creationNonces.get(key) || window.sessionStorage.getItem(key) || undefined;
  } catch {
    return creationNonces.get(key);
  }
};
export const onboardingCreationNonce = (email) => {
  const key = creationKey(email);
  let nonce = retainedOnboardingCreationNonce(email);
  if (!nonce) {
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    nonce = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
    creationNonces.set(key, nonce);
    try {
      window.sessionStorage.setItem(key, nonce);
    } catch {
      /* memory fallback */
    }
  }
  return nonce;
};
const documentCapabilities = new Map();
const rememberDocumentCapability = (token) => {
  try {
    // Decode only for local lookup; the server verifies the signature and expiry.
    const payload = token.split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(window.atob(payload));
    const code = createTranslator().fromUUID(claims.request_id);
    const key = `upestate-document:${code}`;
    documentCapabilities.set(key, token);
    try {
      window.sessionStorage.setItem(key, token);
    } catch {
      /* memory fallback */
    }
  } catch {
    /* Not a usable application capability. */
  }
};
export const documentRequestOptions = (code) => {
  const key = `upestate-document:${code}`;
  let token = documentCapabilities.get(key);
  try {
    token = token || window.sessionStorage.getItem(key);
  } catch {
    /* memory fallback */
  }
  try {
    const claims = JSON.parse(
      window.atob(token.split('.')[0].replace(/-/g, '+').replace(/_/g, '/'))
    );
    if (claims.exp <= Math.floor(Date.now() / 1000)) token = undefined;
  } catch {
    token = undefined;
  }
  return token
    ? { headers: { Authorization: `Bearer ${token}` }, upestateOnboarding: true }
    : undefined;
};
const documentIdentities = new Map();
export const documentIdentity = (code) => {
  if (!documentIdentities.has(code)) documentIdentities.set(code, documentIdentities.size + 1);
  return `${documentIdentities.get(code)}:${Boolean(documentRequestOptions(code))}`;
};
